import { useCreateCompanyBankDetailMutation } from "Company/Modules/BankDetail/Api/Mutation/useCreateCompanyBankDetailMutation";
import {
	type CompanyBankDetailCreateFormModel,
	getCompanyBankDetailCreateForm,
} from "Company/Modules/BankDetail/Components/Form/Schema/companyBankDetailCreateFormSchema";
import { Bank, PreferredAccountFormat } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Close } from "@mui/icons-material";
import { Button, Grid2, Stack } from "@mui/material";
import type { FC } from "react";
import { useForm } from "react-hook-form";

type Props = {
	companyId: number;
	closeModal?: () => void;
};

const defaultFormValues = {
	name: "",
	bankName: Bank.fio,
	bankAccount: "",
	iban: "",
};

const bankDetailFormResource = Resources.base.bankDetail.form;
const bankDetailSelectOptions = Resources.base.bankDetail.selectOptions;

export const BankDetailCreateForm: FC<Props> = ({ companyId, closeModal }) => {
	const { t } = useResource();
	const { mutate: createBankDetail, isPending } =
		useCreateCompanyBankDetailMutation(companyId);

	const form = useForm<CompanyBankDetailCreateFormModel>({
		resolver: zodResolver(getCompanyBankDetailCreateForm(t)),
		defaultValues: defaultFormValues,
	});

	const {
		control,
		formState: { errors },
	} = form;

	const { handleServerError } = useHandleServerError(form);

	const handleSubmitCreateBankDetail = () => {
		handleServerError(createBankDetail, {
			onSuccess: () => {
				closeModal?.();
			},
		});
	};
	return (
		<Stack spacing={2}>
			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Input
						id="name"
						name="name"
						label={t(bankDetailFormResource.name)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Input
						id="bank-account"
						name="bankAccount"
						label={t(bankDetailFormResource.bankAccount)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Select
						id="bank-name"
						name="bankName"
						label={t(bankDetailFormResource.bankName)}
						variant="outlined"
						control={control}
						errors={errors}
						options={createSelectOptions(
							Bank,
							t,
							bankDetailSelectOptions.banks,
						)}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Input
						id="iban"
						name="iban"
						label={t(bankDetailFormResource.iban)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<Input
						id="bank-code"
						name="bankCode"
						label={t(bankDetailFormResource.bankCode)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Select
						id="preferred-account-format"
						name="preferredAccountFormat"
						label={t(bankDetailFormResource.preferredAccountFormat)}
						variant="outlined"
						control={control}
						errors={errors}
						options={createSelectOptions(
							PreferredAccountFormat,
							t,
							bankDetailSelectOptions.preferredAccountFormat,
						)}
						fullWidth
					/>
				</Grid2>
			</Grid2>

			<Stack flexDirection="row" columnGap={1} alignSelf="flex-end">
				{closeModal && (
					<Button startIcon={<Close />} onClick={closeModal} color="error">
						{t(bankDetailFormResource.cancel)}
					</Button>
				)}
				<Button
					color="success"
					onClick={handleSubmitCreateBankDetail}
					loading={isPending}
				>
					{t(bankDetailFormResource.save)}
				</Button>
			</Stack>
		</Stack>
	);
};
